import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import DealCard from "../components/DealCard";
import StockQuotes from "../components/StockQuotes";
import { useLocation } from "../lib/LocationContext";
import { MapPin, HeartPulse, ArrowUpDown } from "lucide-react";

const HEALTH_ACCENT = "#3D8B7B"; // muted forest / recovery green

// Editorial intros — 2-3 sentences of curator voice per category
const INTROS = {
  travel: "The best of the road, sky and sea — vetted by editors who've earned their frequent-flier miles the hard way. Every deal here beats the shoulder-season averages, and we won't include a hotel we wouldn't book ourselves.",
  clothing: "Timeless closet staples from labels we actually own, plus the occasional editorial splurge worth marking down. If it's here, it's the lowest price we've spotted this quarter.",
  auto: "New models, certified pre-owned, and the referral programs that never make the front page. Financing offers vetted against KBB fair-market data.",
  stocks: "Broker signup bonuses, transfer promos and platform perks — the kind of small tailwinds that compound. Read the terms; we've done our part.",
  crypto: "Exchange promos, hardware-wallet bundles and yield boosts, filtered for real utility. No memecoin airdrops, no rug-pulls — just the mainstream deals worth knowing.",
  electronics: "Doorbusters on the gear our editors already use and the flagship refreshes worth the wait. Prices tracked against Amazon lows so you know what a real deal looks like.",
  home: "Everything that makes a house work — decor and design finds, DIY tools and hardware, appliance blowouts, lawn care staples and outdoor landscaping essentials. Curated for weekend warriors and design nerds alike.",
  food: "Meal-kit intros, delivery-app trials and grocery-membership perks that actually pay for themselves. Tested by hungry editors on deadline.",
  beauty: "The moisturizers, mascaras and tools we reach for on shoot days — with the sales you'll want to bookmark before they end.",
  gaming: "Console pass renewals, storefront-wide sales and hardware bundles that stretch the collection budget. AAA to indie, no fluff.",
  health: "Movement studios, mindfulness apps and recovery hardware our editors have personally rotated through this month. The ones worth your Sundays.",
  books: "Physical books, ebooks, audiobooks and the streaming services that keep the shelves refreshed. From literary hardcovers to comic bundles and film-buff subscriptions — deals for every kind of reader, watcher and listener.",
};

// Parse a discount string like "40% OFF", "$50 OFF", "50% + BUNDLE" into a numeric score for sorting.
function discountScore(discount) {
  if (!discount) return 0;
  const s = String(discount).toUpperCase();
  const pct = s.match(/(\d+)\s*%/);
  if (pct) return parseInt(pct[1], 10);
  const dollar = s.match(/\$\s*([\d,]+)/);
  if (dollar) {
    const num = parseInt(dollar[1].replace(/,/g, ""), 10);
    return Math.min(80, Math.round(num / 20));
  }
  if (/FREE/.test(s)) return 55;
  if (/BUNDLE|BONUS/.test(s)) return 25;
  return 10;
}

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "biggest", label: "Biggest savings" },
  { key: "rating", label: "Highest rated" },
  { key: "expiring", label: "Ending soon" },
];

const SORT_STORAGE_KEY = "dealhub.category.sort";

function loadSortPref() {
  try {
    const v = localStorage.getItem(SORT_STORAGE_KEY);
    if (v && SORTS.some((s) => s.key === v)) return v;
  } catch (e) { /* ignore */ }
  return "newest";
}

export default function Category() {
  const { slug } = useParams();
  const { city } = useLocation();
  const [deals, setDeals] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(loadSortPref);

  useEffect(() => {
    try { localStorage.setItem(SORT_STORAGE_KEY, sortKey); } catch (e) { /* ignore */ }
  }, [sortKey]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const dealsReq = slug === "health"
          ? api.get("/deals/near", { params: { category: slug, city: city || undefined } })
          : api.get("/deals", { params: { category: slug } });
        const [d, cats] = await Promise.all([
          dealsReq,
          api.get("/deals/categories"),
        ]);
        setDeals(d.data);
        setMeta(cats.data.find((c) => c.slug === slug));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, city]);

  const sorted = useMemo(() => {
    const arr = [...deals];
    if (sortKey === "biggest") {
      arr.sort((a, b) => discountScore(b.discount) - discountScore(a.discount));
    } else if (sortKey === "rating") {
      arr.sort((a, b) => {
        const ra = a.avg_rating || 0;
        const rb = b.avg_rating || 0;
        if (rb !== ra) return rb - ra;
        return (b.review_count || 0) - (a.review_count || 0);
      });
    } else if (sortKey === "expiring") {
      arr.sort((a, b) => {
        const ea = a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
        const eb = b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
        return ea - eb;
      });
    }
    return arr;
  }, [deals, sortKey]);

  const isHealth = slug === "health";
  const accent = isHealth ? HEALTH_ACCENT : "#E63946";
  const intro = INTROS[slug];

  return (
    <div className={`${isHealth ? "bg-[#F3F8F5]" : ""}`}>
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid={`category-page-${slug}`}>
        {isHealth && (
          <div className="mb-10 border-l-4 pl-5" style={{ borderColor: accent }} data-testid="wellness-hero">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <HeartPulse className="w-3.5 h-3.5" strokeWidth={2} />
              Recovery · Movement · Mind
            </div>
            <Link to="/" className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-black">
              ← Back to home
            </Link>
            <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter mt-3">
              Health & <span className="italic font-normal" style={{ color: accent }}>Wellness</span>
            </h1>
            {intro && <p className="text-black/60 mt-3 max-w-2xl" data-testid={`intro-${slug}`}>{intro}</p>}
            {city && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: accent }} data-testid="health-location-hint">
                <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                Prioritized for {city}
              </div>
            )}
          </div>
        )}

        {!isHealth && (
          <div className="mb-10">
            <Link to="/" className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]">
              ← Back to home
            </Link>
            <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter mt-4">
              {meta?.name || slug} <span className="italic font-normal text-black/40">deals</span>
            </h1>
            {intro && <p className="text-black/60 mt-3 max-w-2xl" data-testid={`intro-${slug}`}>{intro}</p>}
          </div>
        )}

        {slug === "stocks" && <StockQuotes />}

        {/* Sort bar */}
        {deals.length > 1 && (
          <div className="flex items-center justify-between mb-6 border-b border-black/10 pb-3" data-testid="sort-bar">
            <div className="text-[10px] font-mono uppercase tracking-widest text-black/50">
              {deals.length} {deals.length === 1 ? "deal" : "deals"}
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-3.5 h-3.5 text-black/40" strokeWidth={2} />
              <label className="text-[10px] font-bold uppercase tracking-widest text-black/50 hidden sm:inline">Sort</label>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="border border-black/20 hover:border-black bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest cursor-pointer"
                data-testid="sort-select"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-black/40 font-mono uppercase tracking-widest text-xs">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="no-deals-empty">
            <div className="font-serif text-3xl mb-2">Nothing here yet</div>
            <p className="text-black/60 text-sm">Our editors are still hunting. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((d) => (
              <DealCard key={d.id} deal={d} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
